/**
 * Nested schema for platform_invoice_templates.template_config.
 * Matches the admin template editor (General, Header & Footer, …).
 */

/** Client-side limit — must stay ≤ backend admin_assets_gcs_service.MAX_ADMIN_IMAGE_BYTES */
export const IMAGE_UPLOAD_MAX_BYTES = 5 * 1024 * 1024;

export const PDF_FONT_OPTIONS = [
  { value: 'Ubuntu', label: 'Ubuntu' },
  { value: 'Helvetica', label: 'Helvetica' },
  { value: 'DejaVu Sans', label: 'DejaVu Sans' },
] as const;

export const IMAGE_POSITION_OPTIONS = [
  { value: 'center_center', label: 'Center center' },
  { value: 'top_center', label: 'Top center' },
  { value: 'bottom_center', label: 'Bottom center' },
  { value: 'top_left', label: 'Top left' },
  { value: 'top_right', label: 'Top right' },
] as const;

/** Labels and copy for the “bill to / meta” block (preview + future PDF) */
export type TransactionDetailsLabels = {
  bill_to_heading: string;
  place_of_supply_label: string;
  invoice_number_label: string;
  invoice_date_label: string;
  terms_label: string;
  due_date_label: string;
  payment_details_label?: string;
  /** When invoice is shown as paid, replaces Terms column label */
  payment_status_label: string;
  paid_in_full_text: string;
  /** Shown in Due Date cell when paid */
  due_date_paid_placeholder: string;
};

/** Line-items table column titles */
export type TableColumnLabels = {
  col_no: string;
  col_description: string;
  col_duration?: string;
  col_qty: string;
  col_rate: string;
  col_cgst: string;
  col_second_tax: string;
  col_amount: string;
  /** If false, hide the second tax column (e.g. UTGST/SGST) in preview */
  show_second_tax_column: boolean;
};

/** Totals block wording */
export type TotalSectionLabels = {
  total_in_words_label: string;
  subtotal_label: string;
  total_label: string;
  amount_paid_label: string;
  balance_due_label: string;
  balance_due_zero: string;
};

/** Extra copy not covered elsewhere */
export type OtherSection = {
  remarks_heading: string;
  remarks_body: string;
};

export type InvoiceTemplateConfig = {
  /** Schema version for future migrations */
  editor_version: number;
  /** Top-bar preset (maps colors in preview; PDF phase can read this) */
  color_theme: string;
  /** Legacy / shared copy still used by preview + future PDF */
  invoice_title: string;
  company_legal_name: string;
  company_address: string;
  tax_id_label: string;
  tax_id_value: string;
  logo_url: string;
  footer_legal_text: string;
  /** Bank / UPI block label, e.g. PAY TO : */
  pay_to_heading: string;
  /** Multiline payee & bank details (no default vendor name). */
  pay_to_content: string;
  /** Line below pay-to block, e.g. thank-you message */
  thank_you_message: string;
  /** Post-payment invoices: show as settled in preview/PDF */
  show_invoice_as_paid: boolean;
  /** Short label on the paid badge */
  paid_badge_label: string;
  primary_color: string;
  show_tax_breakdown: boolean;
  general: {
    template_properties: {
      paper_size: 'A4' | 'A5' | 'Letter';
      orientation: 'portrait' | 'landscape';
      margins_inches: { top: number; bottom: number; left: number; right: number };
      include_payment_stub: boolean;
    };
    font: {
      pdf_font: string;
      label_color: string;
      font_color: string;
      font_size_pt: number;
    };
    background: {
      /** Prefer URL for production; data URL optional for small assets */
      image_url: string;
      image_data_url: string;
      image_position: string;
      use_background_color: boolean;
      background_color: string;
    };
  };
  header_footer: {
    header: {
      image_url: string;
      image_data_url: string;
      image_position: string;
      use_background_color: boolean;
      background_color: string;
      apply_first_page_only: boolean;
      header_height_first_page_in: number;
      header_content_note: string;
      header_other_pages_note: string;
    };
    footer: {
      font_size_pt: number;
      font_color: string;
      image_url: string;
      image_data_url: string;
      image_position: string;
      use_background_color: boolean;
      background_color: string;
      show_page_number: boolean;
      footer_content_note: string;
    };
  };
  transaction_details: TransactionDetailsLabels;
  table: TableColumnLabels;
  total: TotalSectionLabels;
  other: OtherSection;
};

export function defaultTemplateConfig(): InvoiceTemplateConfig {
  return {
    editor_version: 1,
    color_theme: 'default',
    invoice_title: 'TAX INVOICE',
    company_legal_name: 'Sahir Web Solutions - A Unit of Elvek Technologies',
    company_address: 'SCO 123-124, Second Floor, Sector 17C, Chandigarh - 160017, India',
    tax_id_label: 'GSTIN',
    tax_id_value: '',
    logo_url: '',
    footer_legal_text:
      'Sahir Web Solutions - A Unit of Elvek Technologies SCO 123-124, Second Floor, Sector 17C, Chandigarh - 160017, India | This is a computer generated invoice and does not require signature.',
    pay_to_heading: 'PAY TO :',
    pay_to_content: '',
    thank_you_message: 'Thank you for your business.',
    show_invoice_as_paid: true,
    paid_badge_label: 'PAID',
    primary_color: '#ea580c',
    show_tax_breakdown: true,
    general: {
      template_properties: {
        paper_size: 'A4',
        orientation: 'portrait',
        margins_inches: { top: 1.4, bottom: 0.2, left: 0.55, right: 0.4 },
        include_payment_stub: false,
      },
      font: {
        pdf_font: 'Ubuntu',
        label_color: '#000000',
        font_color: '#000000',
        font_size_pt: 8,
      },
      background: {
        image_url: '',
        image_data_url: '',
        image_position: 'center_center',
        use_background_color: true,
        background_color: '#ffffff',
      },
    },
    header_footer: {
      header: {
        image_url: '',
        image_data_url: '',
        image_position: 'top_center',
        use_background_color: true,
        background_color: '#ffffff',
        apply_first_page_only: true,
        header_height_first_page_in: 1.5,
        header_content_note: '',
        header_other_pages_note: '',
      },
      footer: {
        font_size_pt: 6,
        font_color: '#aaaaaa',
        image_url: '',
        image_data_url: '',
        image_position: 'bottom_center',
        use_background_color: true,
        background_color: '#ffffff',
        show_page_number: false,
        footer_content_note: '',
      },
    },
    transaction_details: {
      bill_to_heading: 'Bill To',
      place_of_supply_label: 'Place Of Supply',
      invoice_number_label: 'Invoice No.',
      invoice_date_label: 'Invoice Date',
      terms_label: 'Terms',
      due_date_label: 'Due Date',
      payment_details_label: 'Payment Details',
      payment_status_label: 'Payment',
      paid_in_full_text: 'Paid in full',
      due_date_paid_placeholder: '—',
    },
    table: {
      col_no: 'No.',
      col_description: 'Particulars & Description',
      col_duration: 'Duration',
      col_qty: 'Qty',
      col_rate: 'Rate',
      col_cgst: 'CGST',
      col_second_tax: 'UTGST',
      col_amount: 'Amount',
      show_second_tax_column: true,
    },
    total: {
      total_in_words_label: 'Total In Words:',
      subtotal_label: 'Sub Total',
      total_label: 'Total',
      amount_paid_label: 'Amount Paid',
      balance_due_label: 'Balance Due',
      balance_due_zero: '₹0.00',
    },
    other: {
      remarks_heading: '',
      remarks_body: '',
    },
  };
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/** Deep-merge saved JSON with defaults so new keys appear without breaking old rows */
export function normalizeTemplateConfig(raw: unknown): InvoiceTemplateConfig {
  const base = defaultTemplateConfig();
  if (!isPlainObject(raw)) return base;

  const g = isPlainObject(raw.general) ? raw.general : {};
  const gtp = isPlainObject(g.template_properties) ? g.template_properties : {};
  const gf = isPlainObject(g.font) ? g.font : {};
  const gb = isPlainObject(g.background) ? g.background : {};
  const hf = isPlainObject(raw.header_footer) ? raw.header_footer : {};
  const hh = isPlainObject(hf.header) ? hf.header : {};
  const hft = isPlainObject(hf.footer) ? hf.footer : {};
  const mi = isPlainObject(gtp.margins_inches) ? gtp.margins_inches : {};
  const td = isPlainObject(raw.transaction_details) ? raw.transaction_details : {};
  const tb = isPlainObject(raw.table) ? raw.table : {};
  const tl = isPlainObject(raw.total) ? raw.total : {};
  const ot = isPlainObject(raw.other) ? raw.other : {};

  return {
    ...base,
    ...raw,
    editor_version: typeof raw.editor_version === 'number' ? raw.editor_version : base.editor_version,
    color_theme: typeof raw.color_theme === 'string' ? raw.color_theme : base.color_theme,
    invoice_title: String(raw.invoice_title ?? base.invoice_title),
    company_legal_name: String(raw.company_legal_name ?? base.company_legal_name),
    company_address: String(raw.company_address ?? base.company_address),
    tax_id_label: String(raw.tax_id_label ?? base.tax_id_label),
    tax_id_value: String(raw.tax_id_value ?? base.tax_id_value),
    logo_url: String(raw.logo_url ?? base.logo_url),
    footer_legal_text: String(raw.footer_legal_text ?? base.footer_legal_text),
    pay_to_heading: String(raw.pay_to_heading ?? base.pay_to_heading),
    pay_to_content: String(raw.pay_to_content ?? base.pay_to_content),
    thank_you_message: String(raw.thank_you_message ?? base.thank_you_message),
    show_invoice_as_paid: Boolean(raw.show_invoice_as_paid ?? base.show_invoice_as_paid),
    paid_badge_label: String(raw.paid_badge_label ?? base.paid_badge_label),
    primary_color: String(raw.primary_color ?? base.primary_color),
    show_tax_breakdown: Boolean(raw.show_tax_breakdown ?? base.show_tax_breakdown),
    general: {
      template_properties: {
        ...base.general.template_properties,
        ...gtp,
        paper_size: (gtp.paper_size as InvoiceTemplateConfig['general']['template_properties']['paper_size']) ?? base.general.template_properties.paper_size,
        orientation:
          (gtp.orientation as InvoiceTemplateConfig['general']['template_properties']['orientation']) ??
          base.general.template_properties.orientation,
        margins_inches: {
          ...base.general.template_properties.margins_inches,
          ...mi,
        },
        include_payment_stub: Boolean(
          gtp.include_payment_stub ?? base.general.template_properties.include_payment_stub,
        ),
      },
      font: { ...base.general.font, ...gf },
      background: { ...base.general.background, ...gb },
    },
    header_footer: {
      header: { ...base.header_footer.header, ...hh },
      footer: { ...base.header_footer.footer, ...hft },
    },
    transaction_details: {
      ...base.transaction_details,
      ...td,
      bill_to_heading: String(td.bill_to_heading ?? base.transaction_details.bill_to_heading),
      place_of_supply_label: String(
        td.place_of_supply_label ?? base.transaction_details.place_of_supply_label,
      ),
      invoice_number_label: String(
        td.invoice_number_label ?? base.transaction_details.invoice_number_label,
      ),
      invoice_date_label: String(td.invoice_date_label ?? base.transaction_details.invoice_date_label),
      terms_label: String(td.terms_label ?? base.transaction_details.terms_label),
      due_date_label: String(td.due_date_label ?? base.transaction_details.due_date_label),
      payment_details_label: String(
        td.payment_details_label ?? base.transaction_details.payment_details_label ?? 'Payment Details',
      ),
      payment_status_label: String(
        td.payment_status_label ?? base.transaction_details.payment_status_label,
      ),
      paid_in_full_text: String(td.paid_in_full_text ?? base.transaction_details.paid_in_full_text),
      due_date_paid_placeholder: String(
        td.due_date_paid_placeholder ?? base.transaction_details.due_date_paid_placeholder,
      ),
    },
    table: {
      ...base.table,
      ...tb,
      col_no: String(tb.col_no ?? base.table.col_no),
      col_description: String(tb.col_description ?? base.table.col_description),
      col_duration: String(tb.col_duration ?? base.table.col_duration ?? 'Duration'),
      col_qty: String(tb.col_qty ?? base.table.col_qty),
      col_rate: String(tb.col_rate ?? base.table.col_rate),
      col_cgst: String(tb.col_cgst ?? base.table.col_cgst),
      col_second_tax: String(tb.col_second_tax ?? base.table.col_second_tax),
      col_amount: String(tb.col_amount ?? base.table.col_amount),
      show_second_tax_column: Boolean(
        tb.show_second_tax_column ?? base.table.show_second_tax_column,
      ),
    },
    total: {
      ...base.total,
      ...tl,
      total_in_words_label: String(tl.total_in_words_label ?? base.total.total_in_words_label),
      subtotal_label: String(tl.subtotal_label ?? base.total.subtotal_label),
      total_label: String(tl.total_label ?? base.total.total_label),
      amount_paid_label: String(tl.amount_paid_label ?? base.total.amount_paid_label),
      balance_due_label: String(tl.balance_due_label ?? base.total.balance_due_label),
      balance_due_zero: String(tl.balance_due_zero ?? base.total.balance_due_zero),
    },
    other: {
      ...base.other,
      ...ot,
      remarks_heading: String(ot.remarks_heading ?? base.other.remarks_heading),
      remarks_body: String(ot.remarks_body ?? base.other.remarks_body),
    },
  };
}

export function templateConfigToRecord(cfg: InvoiceTemplateConfig): Record<string, unknown> {
  return { ...cfg } as unknown as Record<string, unknown>;
}

export async function readImageDataUrl(file: File, maxBytes = IMAGE_UPLOAD_MAX_BYTES): Promise<string> {
  if (file.size > maxBytes) {
    throw new Error(`File must be ≤ ${Math.round(maxBytes / 1024)} KB`);
  }
  if (!file.type.startsWith('image/')) {
    throw new Error('Please choose an image (GIF, PNG, JPEG, JPG, BMP)');
  }
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = () => reject(new Error('Could not read file'));
    r.readAsDataURL(file);
  });
}
